/**
 * Ramp Embed - API Layer
 */
import {
  type EmbedConfig,
  EmbedError,
  type EmbedInitTokenPayload,
  type ExchangeInitTokenRequest,
  type ExchangeInitTokenResponse,
  type ExchangeSessionTokenRequest,
  type ExchangeSessionTokenResponse,
} from "./types";

let config: EmbedConfig | null = null;

export function initApi(cfg: EmbedConfig): void {
  config = cfg;
}

function getConfig(): EmbedConfig {
  if (!config) {
    throw new EmbedError("UNKNOWN", "API not initialized");
  }

  return config;
}

// =============================================================================
// JWT
// =============================================================================

export function decodeJwt<T>(token: string): T {
  const parts = token.split(".");
  if (parts.length !== 3 || !parts[1]) {
    throw new EmbedError("INVALID_TOKEN", "Invalid JWT format");
  }

  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64)) as T;
  } catch {
    throw new EmbedError("INVALID_TOKEN", "Failed to decode JWT");
  }
}

export function validateInitToken(
  payload: EmbedInitTokenPayload,
  origin: string
): void {
  if (payload.parent_origin !== origin) {
    throw new EmbedError("ORIGIN_MISMATCH", "Origin mismatch");
  }

  if (payload.exp < Math.floor(Date.now() / 1000)) {
    throw new EmbedError("TOKEN_EXPIRED", "Token expired");
  }
}

// =============================================================================
// API Calls
// =============================================================================

export async function exchangeInitToken(
  request: ExchangeInitTokenRequest
): Promise<ExchangeInitTokenResponse> {
  return post(
    "/v1/public/embedded/init-token/exchange",
    request,
    "EXCHANGE_FAILED"
  );
}

export async function exchangeSessionToken(
  request: ExchangeSessionTokenRequest
): Promise<ExchangeSessionTokenResponse> {
  return post(
    "/v1/public/embedded/session-token/exchange",
    request,
    "ISSUER_TOKEN_FAILED"
  );
}

// =============================================================================
// HTTP
// =============================================================================

const REQUEST_TIMEOUT_MS = 15000;

async function post<T>(
  path: string,
  body: unknown,
  errorCode: "EXCHANGE_FAILED" | "ISSUER_TOKEN_FAILED"
): Promise<T> {
  const { apiBaseUrl } = getConfig();
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (err) {
    clearTimeout(timeoutId);
    if (err instanceof Error && err.name === "AbortError") {
      throw new EmbedError("NETWORK_ERROR", "Request timed out", true);
    }
    throw new EmbedError("NETWORK_ERROR", "Network request failed", true);
  }

  clearTimeout(timeoutId);

  if (!response.ok) {
    const message = await extractErrorMessage(response);
    throw new EmbedError(errorCode, message);
  }

  return response.json();
}

async function extractErrorMessage(response: Response): Promise<string> {
  try {
    const body = await response.json();
    return (
      body.error_v2?.message ??
      body.error?.message ??
      `Request failed (${response.status})`
    );
  } catch {
    return `Request failed (${response.status})`;
  }
}
